import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Logout,
  HomeWork,
  Send,
  ArrowBack,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { createRequest } from '../services/dataServiceWrapper';
import { getLRApprovers, getGraphClient } from '../services/graphService';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production'
  ? 'https://apps.acom.edu.au/api'
  : 'http://localhost:5000/api';

const checklistQuestions = [
  { id: 9, category: 'Workstation', text: 'Do you confirm that when working at home, your work can be carried out independently, and that your responsibilities can be met?' },
  { id: 10, category: 'Chair', text: 'Does your chair have adequate and adjustable lumbar support?' },
  { id: 11, category: 'Chair', text: 'Can the seat back be adjusted up and down and tilted?' },
  { id: 12, category: 'Chair', text: 'If the chair has armrests, are they adjustable to allow the chair to sit close to the desk' },
  { id: 13, category: 'Desk', text: 'Does your desk height allow you to sit upright with the desk surface at elbow height (with arms close your side)' },
  { id: 14, category: 'Desk', text: 'Is there sufficient depth to position monitor(s) for your visual comfort?' },
  { id: 15, category: 'Desk', text: 'Is there adequate leg space under the workstation and can your feet be flat on the ground' },
  { id: 16, category: 'Desk', text: 'Are most frequently used items on the desk within reach?' },
  { id: 17, category: 'Desk', text: 'Do you have an external mouse and keyboard?' },
  { id: 18, category: 'Desk', text: 'Is your monitor adjustable to accommodate height and visual needs' },
  { id: 19, category: 'Environment', text: 'Is the lighting sufficient for the task being performed, easy to see and comfortable on the eyes?' },
  { id: 20, category: 'Environment', text: 'Can glare and reflections be controlled?' },
  { id: 21, category: 'Environment', text: 'Can ventilation and temperature be controlled?' },
  { id: 22, category: 'Environment', text: 'Is your work area free from excessive noise?' },
  { id: 23, category: 'Environment', text: 'Are you working in the ground floor?' },
  { id: 24, category: 'Environment', text: 'Is there adequate leg space under the workstation and can your feet be flat on the ground' },
  { id: 25, category: 'Environment', text: 'Are most frequently used items on the desk within reach?' },
  { id: 26, category: 'Environment', text: 'Do you have an external mouse and keyboard?' },
  { id: 27, category: 'Environment', text: 'Is your monitor adjustable to accommodate height and visual needs' },
  { id: 28, category: 'Emergency', text: 'Are emergency numbers readily available?' },
  { id: 29, category: 'Emergency', text: 'The route from workspace to a safe outdoor location is clean and clear in the case of fire? (Or in the event of body corporate, that there is an agreement to ensure the route is clean and clear)' },
  { id: 30, category: 'Emergency', text: 'A suitable first aid kit is readily available?' },
  { id: 31, category: 'Emergency', text: 'A fire extinguisher or blanket is able to be used to extinguish minor fires?' },
  { id: 32, category: 'Emergency', text: 'A smoke detector is installed and properly maintained?' },
  { id: 33, category: 'Electrical', text: 'Have you carried out a visual inspection of electrical equipment and confirm there are no cuts, nicks, exposed conductors or any visible damage to electrical cabling and equipment?' },
  { id: 34, category: 'Electrical', text: 'Is there a safety switch installed?' },
  { id: 35, category: 'Electrical', text: 'Do you confirm that power outlets are not overloaded?' },
];

const WFHChecklist = () => {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const account = accounts[0];
  const [loading, setLoading] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState('');
  const [answers, setAnswers] = useState(
    checklistQuestions.reduce((acc, q) => ({ ...acc, [q.id]: false }), {})
  );
  const [comments, setComments] = useState('');

  useEffect(() => {
    loadManagers();
  }, [account]);

  const loadManagers = async () => {
    try {
      const graphClient = getGraphClient(instance, account);
      const approvers = await getLRApprovers(graphClient);
      setManagers(approvers);
    } catch (error) {
      console.error('Error loading managers:', error);
      toast.error('Failed to load managers list');
    } finally {
      setLoadingManagers(false);
    }
  };

  const handleToggle = (id) => {
    setAnswers({ ...answers, [id]: !answers[id] });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!selectedManager) {
      toast.error('Please select a manager');
      return;
    }

    setLoading(true);

    try {
      const userEmail = account?.email || account?.username || 'current.user@acom.edu.au';
      const userName = account?.name || userEmail.split('@')[0];

      // Check if all questions are answered "Yes" (checked)
      const allChecked = checklistQuestions.every(q => answers[q.id] === true);

      // Get questions that were answered "No" (unchecked)
      const uncheckedQuestions = checklistQuestions
        .filter(q => !answers[q.id])
        .map(q => `Question ${q.id}: ${q.text}`);

      const wfhRequest = {
        id: Date.now().toString(),
        type: 'wfh',
        title: `WFH Checklist - ${userName}`,
        submittedBy: userEmail,
        submitterName: userName,
        submittedDate: new Date().toISOString(),
        status: 'pending', // Always pending, never auto-approve
        approver: selectedManager,

        // WFH-specific fields
        allChecked: allChecked,
        answers: answers,
        uncheckedQuestions: uncheckedQuestions,
        comments: comments,
        managerEmail: selectedManager,

        documents: [],
      };

      await createRequest(wfhRequest);

      // Send custom WFH notification email
      try {
        await axios.post(`${API_URL}/emails/wfh-checklist-notification`, { request: wfhRequest });
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
        // Continue anyway
      }

      if (allChecked) {
        toast.success('WFH Checklist submitted successfully! All items are compliant.');
      } else {
        toast.success('WFH Checklist submitted. Items requiring attention have been forwarded to your manager.');
      }

      navigate('/my-requests');
    } catch (error) {
      console.error('Error submitting WFH checklist:', error);
      toast.error('Failed to submit WFH checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    navigate('/login');
  };

  // Group questions by category
  const groupedQuestions = checklistQuestions.reduce((acc, q) => {
    if (!acc[q.category]) acc[q.category] = [];
    acc[q.category].push(q);
    return acc;
  }, {});

  const categoryOrder = ['Workstation', 'Chair', 'Desk', 'Environment', 'Emergency', 'Electrical'];
  const uncheckedCount = checklistQuestions.filter(q => !answers[q.id]).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{
                height: '40px',
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">
              {account?.name || account?.username}
            </Typography>
            <Avatar sx={{ bgcolor: '#0288d1' }}>
              {(account?.name || account?.username || 'U')[0].toUpperCase()}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HomeWork sx={{ fontSize: 40, color: '#0288d1', mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0288d1' }}>
              WFH Checklist
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ mb: 3, color: 'text.secondary' }}>
            ACOM is committed to working with staff to support flexible work arrangements (FWA) where possible. This means that it is
            possible for some staff to work from home for periods of time. Should a FWA be agreed, this Work From Home (WFH) form is
            required to be completed annually to ensure your at home environment is safe when considering Workplace Health and Safety legislation.
          </Typography>

          <Typography variant="body1" paragraph sx={{ mb: 4, color: 'text.secondary' }}>
            Upon completion, if there are any areas requiring remediation, this will be summarised for you, and will be forwarded to your
            line manager to support you in this process. If there are no areas requiring remediation, you'll be advised that you have
            completed the checklist satisfactorily, and the information will be forwarded to the Chair of the Quality Assurance Committee.
          </Typography>

          {uncheckedCount > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {uncheckedCount} item{uncheckedCount !== 1 ? 's' : ''} not checked. Items requiring remediation will be forwarded to your manager{selectedManager ? ` (${selectedManager})` : ''}.
            </Alert>
          )}

          {uncheckedCount === 0 && checklistQuestions.length === Object.keys(answers).filter(k => answers[k]).length && (
            <Alert severity="success" sx={{ mb: 3 }}>
              All items checked! Your submission reports compliance.
            </Alert>
          )}

          <form onSubmit={onSubmit}>
            {/* Manager Selection */}
            <Card sx={{ mb: 3, bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Manager
                </Typography>
                <FormControl fullWidth error={!selectedManager}>
                  <InputLabel>Select Manager</InputLabel>
                  <Select
                    value={selectedManager}
                    label="Select Manager"
                    onChange={(e) => setSelectedManager(e.target.value)}
                    disabled={loadingManagers}
                  >
                    {managers.map((manager) => (
                      <MenuItem key={manager.mail || manager.email} value={manager.mail || manager.email}>
                        {manager.displayName || manager.name} ({manager.mail || manager.email})
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedManager && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      Please select your manager
                    </Typography>
                  )}
                </FormControl>
              </CardContent>
            </Card>

            {categoryOrder.map((category) => (
              groupedQuestions[category] && (
                <Card key={category} sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#0288d1' }}>
                      {category}
                      {category === 'Workstation' && (
                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontWeight: 'normal', mt: 0.5 }}>
                          This section confirms that your physical work station is suitable.
                        </Typography>
                      )}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    {groupedQuestions[category].map((question) => (
                      <FormControlLabel
                        key={question.id}
                        control={
                          <Switch
                            checked={answers[question.id] || false}
                            onChange={() => handleToggle(question.id)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body1" sx={{ ml: 1 }}>
                            {question.text}
                          </Typography>
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: 2,
                          ml: 0,
                          width: '100%',
                          border: '1px solid',
                          borderColor: answers[question.id] ? '#4caf50' : '#e0e0e0',
                          borderRadius: 1,
                          p: 2,
                          bgcolor: answers[question.id] ? '#f1f8f4' : 'white',
                          transition: 'all 0.3s',
                        }}
                      />
                    ))}
                  </CardContent>
                </Card>
              )
            ))}

            {/* Comments Section */}
            <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Comments
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Are there any comments you would like to make in regards to any of your responses on this form?
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Enter any additional comments..."
                />
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                sx={{
                  px: 6,
                  py: 1.5,
                  bgcolor: '#0288d1',
                  '&:hover': {
                    bgcolor: '#0277bd',
                  },
                }}
              >
                {loading ? 'Submitting...' : 'Submit Checklist'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © 2025 ACOM Apps Portal. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default WFHChecklist;
